import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { Caret } from '@/shared/components/ui/Caret'
import { createPortal } from 'react-dom'
import { useT } from '@/shared/i18n/useT'

export interface HistoryItem {
  id: number | string
  label: string
}

interface HistorySplitButtonProps {
  direction: 'undo' | 'redo'
  tasks: readonly HistoryItem[]
  onRun: (count: number) => void
  disabled?: boolean
}

export function HistorySplitButton({ direction, tasks, onRun, disabled = false }: HistorySplitButtonProps) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ left: 8, top: 8 })
  const wrapperRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const menu = [...tasks].reverse()
  const label = direction === 'undo' ? t('grid.undo') : t('grid.redo')
  const menuLabel = direction === 'undo' ? t('grid.undoMenu') : t('grid.redoMenu')
  const icon = direction === 'undo' ? (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-[22px] w-[22px] sm:h-[15px] sm:w-[15px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11" />
    </svg>
  ) : (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-[22px] w-[22px] sm:h-[15px] sm:w-[15px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 14 20 9l-5-5" />
      <path d="M20 9H9.5A5.5 5.5 0 0 0 4 14.5v0A5.5 5.5 0 0 0 9.5 20H13" />
    </svg>
  )

  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      const target = event.target as Node
      if (!wrapperRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false)
    }
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const navigateMenu = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
    const items = menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')
    if (!items?.length) return
    const current = Array.from(items).indexOf(document.activeElement as HTMLButtonElement)
    const next = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? items.length - 1
        : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length
    event.preventDefault()
    items[next]?.focus()
  }

  useLayoutEffect(() => {
    if (!open || !wrapperRef.current) return
    const updatePosition = () => {
      const rect = wrapperRef.current?.getBoundingClientRect()
      if (!rect) return
      setMenuPosition({
        left: Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - 296)),
        top: rect.bottom + 4,
      })
    }
    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [open])

  return (
    // Dressed in the TopBar's own palette rather than the page's: transparent over the
    // header colour, with `header-fg`/`header-hover` for the rest. Those tokens follow the
    // brand AND the theme, so this tracks the bar in every combination. No border: it reads
    // as a plain icon in the bar, not a pill stamped on top of it.
    <div ref={wrapperRef} className="relative inline-flex h-9 sm:h-7 overflow-hidden rounded-md bg-transparent text-header-fg">
      <button
        type="button"
        aria-label={label}
        title={label}
        disabled={disabled}
        onClick={() => onRun(1)}
        className="flex h-9 w-8 sm:h-7 sm:w-6 items-center justify-center transition-colors hover:bg-header-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
      >
        {icon}
      </button>
      <button
        type="button"
        aria-label={menuLabel}
        title={menuLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled || menu.length === 0}
        onClick={() => setOpen((value) => !value)}
        className="flex h-9 w-3.5 sm:h-7 items-center justify-center transition-colors hover:bg-header-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
      >
        <Caret />
      </button>
      {open && menu.length > 0 && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label={menuLabel}
          onKeyDown={navigateMenu}
          style={{ position: 'fixed', left: menuPosition.left, top: menuPosition.top }}
          className="z-50 max-h-64 w-72 max-w-[calc(100vw-1rem)] overflow-y-auto overflow-x-hidden rounded-xl border border-border bg-surface p-1 text-xs shadow-xl"
        >
          {menu.map((task, index) => (
            <button
              key={task.id}
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onRun(index + 1) }}
              className="block w-full min-w-0 rounded-xl px-3 py-2 text-left text-fg transition-colors hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            >
              <span className="block truncate">{task.label}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  )
}
